import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Mic,
  Camera,
  Shield,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  LogIn,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import {
  acceptInvite,
  acceptInviteAnonymous,
  cleanTemplateTitle,
  resolveInvitePublic,
  type InvitePublicLanding,
  type SeniorityLevel,
} from '../../api/interview';
import { rememberPostAuthRedirect } from '../../lib/postAuthRedirect';
import { setAnonInviteToken } from '../../lib/anonInvite';

/**
 * Candidate-facing landing for an interview invite (public route).
 *
 * Flow:
 *   1. Resolve invite by raw token  -> show welcome card (anonymous-safe)
 *   2. If not signed in -> "Log in / Sign up" -> redirect back with `next=`
 *   3. If signed in but wrong email -> show "wrong account" prompt
 *   4. If signed in + right email -> consent checkboxes -> accept -> /interview/room/:sessionId
 *
 * Lives outside <AdminGuard>; no admin flag required.
 */
const InviteLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';
  const { isSignedIn, isLoading: authLoading, user, apiCall } = useAuth();

  const [landing, setLanding] = useState<InvitePublicLanding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [consentRecording, setConsentRecording] = useState(false);
  const [consentEval, setConsentEval] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token) {
        setError('Missing token');
        setLoading(false);
        return;
      }
      try {
        const data = await resolveInvitePublic(token);
        if (mounted) setLanding(data);
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [token]);

  const t = useMemo(
    () => ({
      heroTitle: lang === 'de' ? 'Du bist eingeladen' : 'You are invited',
      heroSub:   lang === 'de'
        ? 'Ein AI-geführtes Interview auf Cevee'
        : 'An AI-led interview on Cevee',
      duration:  lang === 'de' ? 'Geschätzte Dauer' : 'Estimated duration',
      minutes:   lang === 'de' ? 'Minuten' : 'minutes',
      hostedBy:  lang === 'de' ? 'Geführt von' : 'Hosted by',
      yourInterviewer: lang === 'de' ? 'Deine AI-Interviewer:in' : 'Your AI interviewer',
      checkList: lang === 'de' ? 'Vorbereitung' : 'Preparation',
      prep1:     lang === 'de' ? 'Ruhige Umgebung, gute Internet-Verbindung' : 'Quiet space, good internet',
      prepMic:   lang === 'de' ? 'Mikrofon' : 'Microphone',
      prepCam:   lang === 'de' ? 'Kamera' : 'Camera',
      prep3:     lang === 'de' ? 'Du kannst jederzeit Pause machen oder abbrechen' : 'You can pause or stop anytime',
      loginCta:  lang === 'de' ? 'Login / Account erstellen' : 'Log in / sign up',
      loginHelp: lang === 'de'
        ? 'Du musst angemeldet sein, um das Interview zu starten. Erstelle einen kostenlosen Cevee Account oder log dich ein.'
        : 'You need to be signed in to start the interview. Create a free Cevee account or log in.',
      consentTitle: lang === 'de' ? 'Einwilligung' : 'Consent',
      // Whether the recruiter's template asks for screen capture in
      // addition to audio + video. Default is TRUE for Cevee templates
      // (migration 028); `?? true` covers older landing payloads that
      // omitted the field before we added it to resolve-by-token.
      consentRecording: lang === 'de'
        ? ((landing?.template?.enable_screen_share ?? true)
            ? 'Ich erlaube die Aufzeichnung von Audio, Video und Bildschirm während des Interviews.'
            : 'Ich erlaube die Audio- und Video-Aufzeichnung des Interviews.')
        : ((landing?.template?.enable_screen_share ?? true)
            ? 'I consent to recording of audio, video and screen during the interview.'
            : 'I consent to audio + video recording of the interview.'),
      consentEval: lang === 'de'
        ? 'Ich erlaube die automatische AI-Bewertung meiner Antworten (Transcript-basiert).'
        : 'I consent to automated AI evaluation of my answers (based on transcript only).',
      consentNote: landing?.is_prescreen
        ? (lang === 'de'
            ? 'Du entscheidest selbst, ob Recruiter:innen dein Ergebnis sehen. Standardmäßig bleibt alles privat.'
            : 'You decide whether recruiters see your result. Everything stays private by default.')
        : (lang === 'de'
            ? 'Eine Recruiterin oder ein Recruiter prüft jede AI-Empfehlung manuell (Human-in-the-Loop).'
            : 'A recruiter reviews every AI recommendation manually (human-in-the-loop).'),
      privacyLink: lang === 'de' ? 'Datenschutz' : 'Privacy notice',
      termsLink:   lang === 'de' ? 'AGB' : 'Terms',
      avvLink:     lang === 'de' ? 'AVV' : 'DPA',
      acceptCta: lang === 'de' ? 'Akzeptieren und bereitmachen' : 'Accept and continue',
      wrongAccountTitle: lang === 'de' ? 'Falscher Account' : 'Wrong account',
      wrongAccountBody:  (invited: string) =>
        lang === 'de'
          ? `Diese Einladung ist für „${invited}". Du bist angemeldet mit einem anderen Account.`
          : `This invite is for "${invited}". You are signed in with a different account.`,
      switchAccount: lang === 'de' ? 'Account wechseln' : 'Switch account',
      expiredTitle: lang === 'de' ? 'Einladung abgelaufen' : 'Invite expired',
      expiredBody: lang === 'de'
        ? 'Diese Einladung ist leider abgelaufen. Bitte kontaktiere das Unternehmen.'
        : 'This invite has expired. Please contact the company.',
      acceptedTitle: lang === 'de' ? 'Bereit zu starten' : 'Ready to start',
      acceptedBody: lang === 'de'
        ? 'Deine Einwilligung ist gespeichert. Du kannst das Interview jetzt starten.'
        : 'Your consent is on file. You can start the interview now.',
      abandonedTitle: lang === 'de'
        ? 'Dieses Interview wurde unterbrochen'
        : 'This interview was interrupted',
      abandonedBody: lang === 'de'
        ? 'Der Tab wurde geschlossen oder die Seite neu geladen, während das Gespräch lief. Aus Fairness- und Aufnahme-Gründen können wir die Session nicht einfach fortsetzen - bitte antworte auf die Einladungs-Email oder kontaktiere dein Recruiting-Team für einen neuen Interview-Link.'
        : 'The tab was closed or the page reloaded while the conversation was live. For fairness and recording integrity we can\'t simply resume the session - please reply to the invitation email or contact your recruiting team for a fresh interview link.',
      abandonedCta: lang === 'de' ? 'Recruiter:in kontaktieren' : 'Contact recruiter',
      // Self-started runs have no invitation email and no recruiting team to
      // fall back on, so they get their own body + CTA. Mirrors the room's
      // AbandonedScreen, which users reach via the in-app exit.
      abandonedBodySelf: lang === 'de'
        ? 'Die Verbindung zum Gespräch wurde unterbrochen - der Tab wurde geschlossen, die Seite neu geladen oder du bist im Browser zurückgegangen. Aus Fairness-Gründen läuft jedes Interview in einem zusammenhängenden Durchgang, deshalb können wir die Session nicht fortsetzen.'
        : 'The connection to the conversation was interrupted - the tab was closed, the page reloaded, or you navigated back. For fairness, every interview runs as a single continuous session, so we can\'t resume this one.',
      abandonedCtaMock: lang === 'de' ? 'Neue Simulation starten' : 'Start a new simulation',
      abandonedHintMock: lang === 'de'
        ? 'Du wirst zur Übersicht weitergeleitet, dort kannst du jederzeit eine neue Simulation starten.'
        : 'You\'ll be taken to your history page where you can start a new simulation any time.',
      abandonedCtaPrescreen: lang === 'de' ? 'Zurück zum Passport' : 'Back to your Passport',
      abandonedHintPrescreen: lang === 'de'
        ? 'Im Intro-Tab deines Passports kannst du das Intro neu starten (im Rahmen deiner verfügbaren Versuche).'
        : 'In the Intro tab of your Passport you can start the Intro again (within your available attempts).',
      abandonedCtaDemo: lang === 'de' ? 'Zurück zum Interview-Hub' : 'Back to interview hub',
      abandonedHintDemo: lang === 'de'
        ? 'Dein Demo-Durchlauf erscheint im Hub unter Ergebnisse, sobald die Aufnahme verarbeitet ist.'
        : 'Your demo run will appear in the hub under Results once processing finishes.',
      completedTitle: lang === 'de' ? 'Interview abgeschlossen' : 'Interview completed',
      completedBody: lang === 'de'
        ? 'Du hast dieses Interview bereits abgeschlossen. Das Unternehmen prüft deine Auswertung - du musst nichts weiter tun.'
        : 'You have already completed this interview. The company reviews your evaluation - nothing more to do.',
      cancelledTitle: lang === 'de' ? 'Einladung zurückgezogen' : 'Invite cancelled',
      cancelledBody: lang === 'de'
        ? 'Die einladende Person hat diese Einladung zurückgezogen.'
        : 'The inviting person has cancelled this invite.',
      startCta: lang === 'de' ? 'Zum Interview-Raum' : 'Go to interview room',
      goToCevee: lang === 'de' ? 'Zu Cevee' : 'Open Cevee',
    }),
    [lang, landing?.is_prescreen],
  );

  if (loading || authLoading) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <p>Loading invite ...</p>
        </div>
      </Shell>
    );
  }

  if (error || !landing) {
    return (
      <Shell>
        <ErrorCard title={lang === 'de' ? 'Ungültige Einladung' : 'Invalid invite'} body={error || 'Unknown error'} />
      </Shell>
    );
  }

  // Terminal states -- show clear UI, never let the candidate try to start.
  if (landing.status === 'completed') {
    return (
      <Shell>
        <WelcomeCard landing={landing} labels={t} lang={lang} />
        <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">{t.completedTitle}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.completedBody}</p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 px-4 py-2 text-sm font-semibold text-slate-700 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
              >
                {t.goToCevee}
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (landing.status === 'cancelled') {
    return (
      <Shell>
        <ErrorCard title={t.cancelledTitle} body={t.cancelledBody} />
      </Shell>
    );
  }

  if (landing.is_expired || landing.status === 'expired') {
    return (
      <Shell>
        <ErrorCard title={t.expiredTitle} body={t.expiredBody} icon={<Clock className="w-6 h-6 text-amber-500" />} />
      </Shell>
    );
  }

  // Lite-ATS warm-intro: the candidate has no Cevee account and takes the
  // interview via the token link. Skip the login + email-match gates and go
  // straight to consent; the room authenticates on the stored invite token.
  const isAnon = !!landing.anonymous;

  // ---- Anonymous (no account yet): render preview + login CTA ----
  if (!isSignedIn && !isAnon) {
    // The invite path needs to be reachable AFTER auth -- not just for
    // the instant /login redirect but also for the signup flow that
    // bounces through email-confirmation (URL gets lost). We persist
    // the target path in localStorage + send it as ?return= for the
    // fast path. The Login page consumes both.
    const target = `/interview/invite/${token}`;
    const ret = encodeURIComponent(target);
    const goAuth = (path: string) => {
      rememberPostAuthRedirect(target);
      navigate(`${path}?return=${ret}`);
    };
    return (
      <Shell>
        <WelcomeCard landing={landing} labels={t} lang={lang} />
        <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-pink-50 flex items-center justify-center text-pink-600 flex-shrink-0">
              <LogIn className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">{t.loginCta}</h3>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{t.loginHelp}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                <button
                  onClick={() => goAuth('/login')}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
                >
                  {lang === 'de' ? 'Einloggen' : 'Log in'}
                </button>
                <button
                  onClick={() => goAuth('/signup')}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
                >
                  {lang === 'de' ? 'Account erstellen' : 'Sign up'}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">
                {lang === 'de' ? 'Eingeladen als ' : 'Invited as '}
                <strong className="text-slate-600">{landing.invited_email}</strong>
              </p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ---- Signed in, but wrong email (skipped for anonymous warm-intro) ----
  const userEmail = (user?.email || '').toLowerCase();
  const invitedEmail = (landing.invited_email || '').toLowerCase();
  if (!isAnon && userEmail !== invitedEmail) {
    return (
      <Shell>
        <WelcomeCard landing={landing} labels={t} lang={lang} />
        <ErrorCard
          title={t.wrongAccountTitle}
          body={t.wrongAccountBody(landing.invited_email)}
        >
          <button
            onClick={() => {
              const target = `/interview/invite/${token}`;
              rememberPostAuthRedirect(target);
              navigate(`/login?return=${encodeURIComponent(target)}`);
            }}
            className="mt-3 px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
          >
            {t.switchAccount}
          </button>
        </ErrorCard>
      </Shell>
    );
  }

  // ---- Abandoned: candidate was inside the call once, then tab closed
  // / refreshed (status='in_progress'). We do NOT offer a resume: the
  // AI's Realtime context is gone and a restart would wipe the
  // transcript, AND we don't want candidates to "rewind" weak answers.
  // Mirror the room's AbandonedScreen exactly so the UX is consistent
  // across surfaces. The room itself enforces the same block via
  // stage='abandoned', and the backend rejects /start with 409
  // 'session_already_in_progress' as defense-in-depth.
  //
  // Note: status='started' (start_session was called but the WS never
  // connected -- candidate bailed during handshake) is NOT blocked here;
  // it falls through to the normal "ready to start" branch below since
  // no actual progress was made and the backend treats it as a no-op
  // restart.
  if (landing.status === 'in_progress') {
    const abandonedDemo = !!landing.is_recruiter_demo;
    const abandonedPreScreen = !!landing.is_prescreen;
    const abandonedMock = !!landing.is_mock && !abandonedDemo && !abandonedPreScreen;
    const selfStarted = abandonedDemo || abandonedPreScreen || abandonedMock;

    const selfCta = abandonedDemo
      ? { label: t.abandonedCtaDemo, hint: t.abandonedHintDemo, to: '/interviews?tab=results' }
      : abandonedPreScreen
        ? { label: t.abandonedCtaPrescreen, hint: t.abandonedHintPrescreen, to: '/?tab=passport&pp=prescreen' }
        : { label: t.abandonedCtaMock, hint: t.abandonedHintMock, to: '/mock/history' };

    return (
      <Shell>
        <WelcomeCard landing={landing} labels={t} lang={lang} />
        <div className="rounded-3xl bg-white border border-amber-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">{t.abandonedTitle}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                {selfStarted ? t.abandonedBodySelf : t.abandonedBody}
              </p>
              {selfStarted ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(selfCta.to)}
                    className="mt-4 inline-flex items-center px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
                  >
                    {selfCta.label}
                  </button>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{selfCta.hint}</p>
                </>
              ) : (
                <a
                  href="mailto:lea.anders@cevee.eu"
                  className="mt-4 inline-flex items-center px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
                >
                  {t.abandonedCta}
                </a>
              )}
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ---- Already accepted (consent_given) -- fresh, ready to enter the room ----
  if (landing.is_accepted || landing.status === 'consent_given') {
    return (
      <Shell>
        <WelcomeCard landing={landing} labels={t} lang={lang} />
        <div className="rounded-3xl bg-white border border-emerald-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">{t.acceptedTitle}</h3>
              <p className="text-sm text-slate-600 mt-1">{t.acceptedBody}</p>
              <button
                onClick={() => {
                  if (isAnon && token) setAnonInviteToken(landing.session_id, token);
                  navigate(`/interview/room/${landing.session_id}`);
                }}
                className="mt-4 px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
              >
                {t.startCta}
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ---- Signed in + matching email + needs consent ----
  const handleAccept = async () => {
    if (!consentRecording || !consentEval || !token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      if (isAnon) {
        // No account: record consent anonymously, then stash the token so the
        // room can authenticate REST + WS without a JWT.
        const res = await acceptInviteAnonymous(token, {
          consent_recording: true,
          consent_ai_evaluation: true,
          consent_locale: lang,
        });
        setAnonInviteToken(res.session_id, token);
        navigate(`/interview/room/${res.session_id}`);
      } else {
        const session = await acceptInvite(apiCall, token, {
          consent_recording: true,
          consent_ai_evaluation: true,
          consent_locale: lang,
        });
        navigate(`/interview/room/${session.id}`);
      }
    } catch (e) {
      setAcceptError(e instanceof Error ? e.message : String(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Shell>
      <WelcomeCard landing={landing} labels={t} lang={lang} />

      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-pink-50 flex items-center justify-center text-pink-600 flex-shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{t.consentTitle}</h3>
            <p className="text-sm text-slate-500 mt-1">{t.consentNote}</p>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-3 p-3 rounded-2xl border border-slate-200 hover:bg-slate-50/60">
          <input
            type="checkbox"
            checked={consentRecording}
            onChange={(e) => setConsentRecording(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-slate-700">{t.consentRecording}</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer mb-4 p-3 rounded-2xl border border-slate-200 hover:bg-slate-50/60">
          <input
            type="checkbox"
            checked={consentEval}
            onChange={(e) => setConsentEval(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-slate-700">{t.consentEval}</span>
        </label>

        {acceptError && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{acceptError}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Three separate legal anchors -- the old single link was
              labelled "Datenschutz" but pointed at /legal/avv, which is
              the DPA (Auftragsverarbeitungsvertrag), not the privacy
              notice. Candidates clicking it were getting the wrong
              document. */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-4 hover:text-slate-700">
              {t.privacyLink}
            </a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-4 hover:text-slate-700">
              {t.termsLink}
            </a>
            <a href="/legal/avv" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-4 hover:text-slate-700">
              {t.avvLink}
            </a>
          </div>
          <button
            disabled={!consentRecording || !consentEval || accepting}
            onClick={handleAccept}
            className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.acceptCta}
          </button>
        </div>
      </div>
    </Shell>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-slate-50/30">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-5">
      <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-2">
        Cevee &middot; AI Interview
      </p>
      {children}
    </div>
  </div>
);

const SENIORITY_LABELS: Record<SeniorityLevel, { de: string; en: string }> = {
  intern:    { de: 'Studi / Werkstudent', en: 'Student / Intern' },
  junior:    { de: 'Junior', en: 'Junior' },
  mid:       { de: 'Mid-Level', en: 'Mid-level' },
  senior:    { de: 'Senior', en: 'Senior' },
  staff:     { de: 'Staff', en: 'Staff' },
  principal: { de: 'Principal', en: 'Principal' },
  lead:      { de: 'Lead / Head', en: 'Lead / Head' },
};

function normalizeInterviewTitle(title: string): string {
  return cleanTemplateTitle(title).toLowerCase().replace(/\s+/g, ' ').trim();
}

const WelcomeCard: React.FC<{
  landing: InvitePublicLanding;
  labels: ReturnType<typeof Object> | any;
  lang?: 'de' | 'en';
}> = ({ landing, labels, lang = 'de' }) => {
  const isDemo = !!landing.is_recruiter_demo;
  const isPreScreen = !!landing.is_prescreen;
  const isMock = !!landing.is_mock && !isDemo && !isPreScreen;
  const contextBanner = isDemo
    ? (lang === 'de'
        ? {
            tone: 'demo' as const,
            badge: 'Demo - Recruiter-Vorschau',
            title: 'Demo-Interview',
            body: 'Das ist deine Demo: Gleiches Template, gleicher Fragenpool, gleiche Tiefe und Persona wie bei deinen Kandidat:innen. Pro Session wird aus dem Pool gewürfelt - die Fragen können also leicht variieren. Zählt nicht auf eine Quota. Die KI-Scorecard erscheint danach in deinem Hub unter "Ergebnisse".',
          }
        : {
            tone: 'demo' as const,
            badge: 'Demo - recruiter preview',
            title: 'Demo interview',
            body: 'This is your demo: same template, question pool, depth and persona as for your candidates. Each session draws from the pool, so the questions may vary slightly. It does not count against any quota. The AI scorecard shows up in your hub under "Results" afterwards.',
          })
    : isPreScreen
    ? (lang === 'de'
        ? {
            tone: 'prescreen' as const,
            badge: 'Cevee Pre-Screen',
            title: 'Cevee Pre-Screen',
            body: 'Du machst ihn selbst. Ergebnis, Transcript und Video bleiben privat - Recruiter:innen sehen sie nur, wenn du sie freigibst.',
          }
        : {
            tone: 'prescreen' as const,
            badge: 'Cevee Pre-Screen',
            title: 'Cevee Pre-Screen',
            body: 'You run it yourself. Result, transcript and video stay private - recruiters only see them if you choose to share.',
          })
    : isMock
    ? (lang === 'de'
        ? {
            tone: 'mock' as const,
            badge: 'Simulation - Übungsmodus',
            title: 'AI-Video-Interview-Simulation',
            body: 'Das ist eine private Übungs-Session - keine Recruiter:in und keine Drittpartei sieht deine Aufnahme oder das Feedback. Am Ende gibt dir die KI kurzes mündliches Feedback, anschließend kannst du dir alles im Verlauf nochmal ansehen.',
          }
        : {
            tone: 'mock' as const,
            badge: 'Simulation - practice mode',
            title: 'AI video interview simulation',
            body: 'This is a private practice session - no recruiter and no third party sees your recording or feedback. The AI gives you brief verbal feedback at the end, then everything lives in your history for replay.',
          })
    : null;

  const badgeStyles: Record<string, string> = {
    prescreen: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    mock: 'text-pink-700 bg-pink-50 border-pink-100',
    demo: 'text-violet-700 bg-violet-50 border-violet-100',
  };
  const badgeDotStyles: Record<string, string> = {
    prescreen: 'bg-emerald-500',
    mock: 'bg-pink-500',
    demo: 'bg-violet-500',
  };
  const panelStyles: Record<string, string> = {
    prescreen: 'bg-emerald-50 border-emerald-100',
    mock: 'bg-pink-50 border-pink-100',
    demo: 'bg-violet-50 border-violet-100',
  };
  const panelTitleStyles: Record<string, string> = {
    prescreen: 'text-emerald-700',
    mock: 'text-pink-700',
    demo: 'text-violet-700',
  };
  const panelBodyStyles: Record<string, string> = {
    prescreen: 'text-emerald-900/80',
    mock: 'text-pink-900/80',
    demo: 'text-violet-900/80',
  };

  // Recruiter-sent anonymous warm-intro: we surface it as "Warm Intro" and
  // never leak the internal template name ("... Get to know") to candidates.
  const isWarmIntro = !!landing.anonymous;
  const roleTitle = cleanTemplateTitle(landing.role_title || '');
  const templateTitle = cleanTemplateTitle(landing.template.title || '');
  const primaryTitle = roleTitle || templateTitle;
  const titlesMatch =
    !!primaryTitle &&
    !!templateTitle &&
    normalizeInterviewTitle(roleTitle || templateTitle) === normalizeInterviewTitle(templateTitle);
  const showTemplateSubtitle = isWarmIntro || (!!templateTitle && !titlesMatch);
  const showCompany =
    !!landing.company_name &&
    !isDemo &&
    !isPreScreen &&
    !isMock &&
    landing.company_name.trim().toLowerCase() !== 'cevee';
  const seniorityLabel = landing.role_seniority
    ? SENIORITY_LABELS[landing.role_seniority]?.[lang === 'de' ? 'de' : 'en'] || landing.role_seniority
    : null;

  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 shadow-[0_2px_8px_rgba(15,23,42,0.04)] overflow-hidden">
      <div className="p-6">
        {contextBanner && (
          <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider border px-2 py-0.5 rounded-full mb-3 ${badgeStyles[contextBanner.tone]}`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${badgeDotStyles[contextBanner.tone]}`} />
            {contextBanner.badge}
          </span>
        )}
        {landing.persona && (
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-12 h-12 rounded-full bg-gradient-to-br ${landing.persona.gradient || 'from-pink-500 to-orange-400'} flex items-center justify-center text-white text-base font-bold flex-shrink-0 shadow-sm`}
            >
              {(landing.persona.name || '?').charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                {labels.hostedBy}
              </p>
              <p className="text-sm font-semibold text-slate-900 truncate">
                {landing.persona.name || '--'}
              </p>
            </div>
          </div>
        )}
        {contextBanner && (
          <div className={`mb-4 p-3 rounded-2xl border ${panelStyles[contextBanner.tone]}`}>
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${panelTitleStyles[contextBanner.tone]}`}>
              {contextBanner.title}
            </p>
            <p className={`text-xs mt-1 leading-relaxed ${panelBodyStyles[contextBanner.tone]}`}>
              {contextBanner.body}
            </p>
          </div>
        )}
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          {isDemo
            ? (lang === 'de' ? 'Bereit für deine Demo?' : 'Ready for your demo?')
            : isPreScreen
            ? (lang === 'de' ? 'Bereit für deinen Pre-Screen?' : 'Ready for your pre-screen?')
            : isMock
            ? (lang === 'de' ? 'Bereit für deine Simulation?' : 'Ready for your simulation?')
            : labels.heroTitle}
        </h1>
        <p className="text-slate-500 mt-0.5">{labels.heroSub}</p>

        {primaryTitle && (
          <div className="mt-4 p-3.5 rounded-2xl bg-slate-50/70 border border-slate-100">
            <div className="flex flex-wrap items-start gap-2">
              <h2 className="text-base font-semibold text-slate-900 leading-snug flex-1 min-w-0">
                {primaryTitle}
              </h2>
              {seniorityLabel && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-slate-600 bg-white border border-slate-200 flex-shrink-0">
                  {seniorityLabel}
                </span>
              )}
            </div>
            {showTemplateSubtitle && (
              <p className="text-xs text-slate-500 mt-1.5">
                {isWarmIntro
                  ? (lang === 'de' ? 'Format: Warm Intro' : 'Format: Warm Intro')
                  : `${lang === 'de' ? 'Interview-Template' : 'Interview template'}: ${templateTitle}`}
              </p>
            )}
            {showCompany && (
              <p className="text-sm text-slate-600 mt-1">{landing.company_name}</p>
            )}
          </div>
        )}

        <div className="mt-5">
          <Stat
            icon={<Clock className="w-4 h-4 text-slate-400" />}
            label={labels.duration}
            value={`~${landing.template.duration_minutes} ${labels.minutes}`}
          />
        </div>

        {landing.invite_message && (
          <div className="mt-4 p-3 rounded-2xl bg-slate-50/70 border border-slate-100 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {landing.invite_message}
          </div>
        )}

        <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-slate-500">
          <Tip icon={<Mic className="w-3.5 h-3.5" />} label={labels.prepMic} />
          <Tip icon={<Camera className="w-3.5 h-3.5" />} label={labels.prepCam} />
          <Tip icon={<Clock className="w-3.5 h-3.5" />} label={labels.prep1} />
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50/60 border border-slate-100">
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  </div>
);

const Tip: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-1.5 p-2 rounded-xl bg-slate-50 border border-slate-100">
    {icon}
    <span className="truncate">{label}</span>
  </div>
);

const ErrorCard: React.FC<{
  title: string;
  body: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, body, icon, children }) => (
  <div className="rounded-3xl bg-white border border-red-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0">
        {icon || <AlertTriangle className="w-5 h-5" />}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600 mt-1">{body}</p>
        {children}
      </div>
    </div>
  </div>
);

export default InviteLanding;
